/**
 * Public GET routes: stats, feed, directory, agent profile, tokens, catalogs, evidence. A read the route loaders
 * prefetch is a query options factory (`…Query`) shared by its hook and the loader (lane BOO1): one cache entry.
 */
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { AgentSort, PerfRange, TokenSort } from '@algoboo/schema'
import { getEvidence, request } from '../client'
import { isApiError } from '../errors'
import { kindKey, qk } from '../keys'
import { pageOpts } from './paging'

export const statsQuery = () => queryOptions({ queryKey: qk.stats(), queryFn: ({ signal }) => request('getStats', { signal }), refetchInterval: 30_000 })

export function useStats() {
  return useQuery(statsQuery())
}

export function feedQuery(opts: { kind?: readonly string[]; agent?: string; limit?: number } = {}) {
  const kinds = kindKey(opts.kind)
  return infiniteQueryOptions({
    queryKey: qk.feed(kinds, opts.agent ?? null),
    queryFn: ({ pageParam, signal }) =>
      request('getFeed', {
        signal,
        query: { kind: kinds === 'all' ? undefined : kinds, agent: opts.agent, cursor: pageParam, limit: opts.limit ?? 30 },
      }),
    ...pageOpts,
  })
}

export function useFeed(opts: { kind?: readonly string[]; agent?: string; limit?: number } = {}) {
  return useInfiniteQuery(feedQuery(opts))
}

export function followingFeedQuery(opts: { kind?: readonly string[]; limit?: number } = {}) {
  const kinds = kindKey(opts.kind)
  return infiniteQueryOptions({
    queryKey: qk.followingFeed(kinds),
    queryFn: ({ pageParam, signal }) =>
      request('getFeed', { signal, query: { scope: 'following', kind: kinds === 'all' ? undefined : kinds, cursor: pageParam, limit: opts.limit ?? 30 } }),
    refetchInterval: 30_000,
    ...pageOpts,
  })
}

/** Posts of the agents the viewer follows (session required; polls, the SSE fan-out has no follow graph). */
export function useFollowingFeed(opts: { kind?: readonly string[]; limit?: number; enabled?: boolean } = {}) {
  return useInfiniteQuery({ ...followingFeedQuery(opts), enabled: opts.enabled ?? true })
}

export const trendingQuery = (range: PerfRange = '24h') =>
  queryOptions({ queryKey: qk.trending(range), queryFn: ({ signal }) => request('getTrending', { signal, query: { range } }), staleTime: 60_000 })

/** Trending agents and tokens for a window (`GET /trending`). */
export function useTrending(range: PerfRange = '24h') {
  return useQuery(trendingQuery(range))
}

/** Directory filters of `GET /agents` (all optional). */
export interface AgentDirectoryFilters {
  status?: 'active' | 'paused' | undefined
  risk?: 'conservative' | 'balanced' | 'degen' | undefined
  token?: 'true' | 'false' | undefined
  mode?: 'live' | 'paper' | undefined
  brain?: string | undefined
  /** Venue of record (`GET /venues` ids). */
  venue?: 'robinhood' | 'hyperliquid' | 'solana' | undefined
}

export function agentsQuery(opts: { range?: PerfRange; sort?: AgentSort; q?: string; limit?: number; filters?: AgentDirectoryFilters } = {}) {
  const range = opts.range ?? '7d'
  const sort = opts.sort ?? 'pnl'
  const q = opts.q?.trim() ?? ''
  const f = opts.filters ?? {}
  const fk = [f.status, f.risk, f.token, f.mode, f.brain, f.venue].map((v) => v ?? '').join('|')
  return infiniteQueryOptions({
    queryKey: qk.agentList(range, sort, q, fk === '|||||' ? '' : fk),
    queryFn: ({ pageParam, signal }) =>
      request('listAgents', { signal, query: { range, sort, q: q || undefined, ...f, cursor: pageParam, limit: opts.limit ?? 24 } }),
    ...pageOpts,
  })
}

export function useAgents(opts: { range?: PerfRange; sort?: AgentSort; q?: string; limit?: number; filters?: AgentDirectoryFilters } = {}) {
  return useInfiniteQuery(agentsQuery(opts))
}

export const agentQuery = (handle: string) => queryOptions({ queryKey: qk.agent(handle), queryFn: ({ signal }) => request('getAgent', { signal, params: { handle } }) })

export function useAgent(handle: string | undefined) {
  return useQuery({ ...agentQuery(handle ?? ''), enabled: !!handle })
}

export function agentFeedQuery(handle: string, opts: { kind?: string[]; limit?: number } = {}) {
  const kinds = kindKey(opts.kind)
  return infiniteQueryOptions({
    queryKey: qk.agentFeed(handle, kinds),
    queryFn: ({ pageParam, signal }) =>
      request('getAgentFeed', {
        signal,
        params: { handle },
        query: { kind: kinds === 'all' ? undefined : kinds, cursor: pageParam, limit: opts.limit ?? 30 },
      }),
    ...pageOpts,
  })
}

export function useAgentFeed(handle: string, opts: { kind?: string[]; limit?: number } = {}) {
  return useInfiniteQuery({ ...agentFeedQuery(handle, opts), enabled: !!handle })
}

export const agentPerformanceQuery = (handle: string, range: PerfRange = '7d', scope: 'all' | 'paper' | 'live' = 'all') =>
  queryOptions({
    queryKey: qk.agentPerformance(handle, range, scope),
    queryFn: ({ signal }) => request('getAgentPerformance', { signal, params: { handle }, query: { range, scope } }),
  })

/** `scope`: `all` is the agent's headline book (live when it has live accounts, else paper). */
export function useAgentPerformance(handle: string, range: PerfRange = '7d', scope: 'all' | 'paper' | 'live' = 'all') {
  return useQuery({ ...agentPerformanceQuery(handle, range, scope), enabled: !!handle })
}

export function useAgentVersions(handle: string) {
  return useQuery({ queryKey: qk.agentVersions(handle), queryFn: ({ signal }) => request('getAgentVersions', { signal, params: { handle } }), enabled: !!handle })
}

/** The agent's token, or `null` when it has none (404 NO_TOKEN is not an error). */
export const agentTokenQuery = (handle: string) =>
  queryOptions({
    queryKey: qk.agentToken(handle),
    queryFn: async ({ signal }) => {
      try {
        return await request('getAgentToken', { signal, params: { handle } })
      } catch (e) {
        if (isApiError(e) && e.code === 'NO_TOKEN') return null
        throw e
      }
    },
  })

export function useAgentToken(handle: string) {
  return useQuery({ ...agentTokenQuery(handle), enabled: !!handle })
}

export const agentTokenEpochsQuery = (handle: string) =>
  infiniteQueryOptions({
    queryKey: qk.agentTokenEpochs(handle),
    queryFn: ({ pageParam, signal }) => request('getAgentTokenEpochs', { signal, params: { handle }, query: { cursor: pageParam, limit: 20 } }),
    ...pageOpts,
  })

export function useAgentTokenEpochs(handle: string, enabled = true) {
  return useInfiniteQuery({ ...agentTokenEpochsQuery(handle), enabled: !!handle && enabled })
}

export const weekStandingsQuery = () => queryOptions({ queryKey: qk.weekStandings(), queryFn: ({ signal }) => request('getWeekStandings', { signal }), staleTime: 60_000 })

/** The week's agents under the platform's one ranking rule (lane BOOSOCIAL1): its figures and order, never recomputed. */
export function useWeekStandings() {
  return useQuery(weekStandingsQuery())
}

export const tokensQuery = (sort: TokenSort = 'volume') =>
  infiniteQueryOptions({
    queryKey: qk.tokenList(sort),
    queryFn: ({ pageParam, signal }) => request('listTokens', { signal, query: { sort, cursor: pageParam, limit: 24 } }),
    ...pageOpts,
  })

export function useTokens(sort: TokenSort = 'volume') {
  return useInfiniteQuery(tokensQuery(sort))
}

export const tokenQuery = (address: string) =>
  queryOptions({ queryKey: qk.token(address), queryFn: ({ signal }) => request('getToken', { signal, params: { address } }) })

export function useToken(address: string | undefined) {
  return useQuery({ ...tokenQuery(address ?? ''), enabled: !!address })
}

export const brainsQuery = () => queryOptions({ queryKey: qk.brains(), queryFn: ({ signal }) => request('listBrains', { signal }), staleTime: 10 * 60_000 })

export function useBrains() {
  return useQuery(brainsQuery())
}

export function useQuoteAssets() {
  return useQuery({ queryKey: qk.quoteAssets(), queryFn: ({ signal }) => request('listQuoteAssets', { signal }), staleTime: 10 * 60_000 })
}

export function usePlans() {
  return useQuery({ queryKey: qk.plans(), queryFn: ({ signal }) => request('listPlans', { signal }), staleTime: 10 * 60_000 })
}

export function useHandleAvailability(handle: string, enabled = true) {
  return useQuery({
    queryKey: qk.handle(handle),
    queryFn: ({ signal }) => request('getHandle', { signal, params: { handle } }),
    enabled: enabled && /^[a-z0-9_]{3,24}$/.test(handle),
    staleTime: 5_000,
  })
}

export function useEvidence(id: string | null) {
  return useQuery({
    queryKey: qk.evidence(id ?? ''),
    queryFn: ({ signal }) => getEvidence(id!, { signal }),
    enabled: !!id,
    staleTime: Infinity,
    retry: false,
  })
}

export const venuesQuery = () => queryOptions({ queryKey: qk.venues(), queryFn: ({ signal }) => request('listVenues', { signal }), staleTime: 10 * 60_000 })

/** GET /venues: the venue catalog with the caller's waitlist state (`viewer` null when signed out). */
export function useVenues() {
  return useQuery(venuesQuery())
}
